import React, { useEffect, useState } from "react";
import { Sparkles, Plus, Pencil, Trash2, X, Globe, EyeOff, CalendarClock } from "lucide-react";
import api from "../../lib/api";

const CATEGORIES = ["Credit Score", "Mortgage Prep", "Credit Building", "Strategy", "Disputes", "Loans"];

const EMPTY = { title: "", slug: "", category: "Credit Score", excerpt: "", cover_image: "", content: "", meta_title: "", meta_description: "", read_time: "", status: "draft" };

export default function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, ...fields}
  const [saving, setSaving] = useState(false);
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [auto, setAuto] = useState(null);
  const [autoRunning, setAutoRunning] = useState(false);

  const reload = () => api.get("/admin/blog/posts").then(({ data }) => setPosts(data.items));
  const reloadAuto = () => api.get("/admin/blog/auto-settings").then(({ data }) => setAuto(data));
  useEffect(() => { reload(); reloadAuto(); }, []);

  const saveAuto = async (patch) => {
    const next = { enabled: auto.enabled, mode: auto.mode || "publish", ...patch };
    setAuto({ ...auto, ...next });
    try { await api.put("/admin/blog/auto-settings", next); await reloadAuto(); }
    catch (e) { alert(e?.response?.data?.detail || "Couldn't save autopilot settings."); }
  };

  const runNow = async () => {
    if (!window.confirm("Generate a new article right now? The AI picks a fresh topic automatically (~30-60s).")) return;
    setAutoRunning(true);
    try {
      const { data } = await api.post("/admin/blog/auto-run");
      alert(`Created: "${data.title}" (${data.status})`);
      await reload(); await reloadAuto();
    } catch (e) { alert(e?.response?.data?.detail || "Generation failed."); }
    finally { setAutoRunning(false); }
  };

  const save = async () => {
    if (!editing.title.trim()) { alert("Title is required."); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/blog/posts/${editing.id}`, editing);
      else await api.post("/admin/blog/posts", editing);
      setEditing(null);
      await reload();
    } catch (e) { alert(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This removes it from the live site.`)) return;
    try { await api.delete(`/admin/blog/posts/${p.id}`); await reload(); }
    catch (e) { alert(e?.response?.data?.detail || "Delete failed."); }
  };

  const togglePublish = async (p) => {
    try {
      await api.put(`/admin/blog/posts/${p.id}`, { ...p, status: p.status === "published" ? "draft" : "published" });
      await reload();
    } catch (e) { alert(e?.response?.data?.detail || "Update failed."); }
  };

  const generate = async () => {
    if (!topic.trim()) { alert("Give the AI a topic first — e.g. 'How to remove collections in Texas'."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/blog/generate", { topic: topic.trim() });
      setEditing({ ...EMPTY, ...data, status: "draft" });
      setTopic("");
    } catch (e) { alert(e?.response?.data?.detail || "AI generation failed. Try again."); }
    finally { setGenerating(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-blog-tab">
      {/* Weekly autopilot */}
      <div className="card-dark p-6" data-testid="blog-autopilot-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <CalendarClock className="w-4 h-4 text-[var(--if-gold)]" />
              <span className="font-mono-label text-[var(--if-gold)]">Weekly Auto Article</span>
              <span className={`px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-semibold ${auto?.enabled ? "bg-green-500/15 text-green-300" : "bg-white/10 text-white/50"}`} data-testid="blog-autopilot-status">
                {auto?.enabled ? "ON" : "OFF"}
              </span>
            </div>
            <p className="text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
              Once a week the AI picks a fresh topic (never repeating existing posts) and writes a full SEO article.
              {auto?.last_run_at && <> Last run: {String(auto.last_run_at).slice(0, 10)}.</>}
              {auto?.last_auto_post && <> Latest: "{auto.last_auto_post.title}" ({auto.last_auto_post.status}).</>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <select className="if-input !w-auto !py-2 !text-xs" value={auto?.mode || "publish"} onChange={(e) => saveAuto({ mode: e.target.value })} data-testid="blog-autopilot-mode">
              <option value="publish">Publish automatically</option>
              <option value="draft">Save as draft for my review</option>
            </select>
            <button onClick={() => saveAuto({ enabled: !auto?.enabled })}
              className={`px-4 py-2 rounded-full border text-[11px] transition-all ${auto?.enabled ? "border-green-500/50 text-green-300 hover:bg-green-500/10" : "border-white/20 text-white/60 hover:border-green-500/40 hover:text-green-300"}`}
              data-testid="blog-autopilot-toggle">
              {auto?.enabled ? "Autopilot: ON" : "Autopilot: OFF"}
            </button>
            <button onClick={runNow} disabled={autoRunning} className="btn-outline !py-2 !px-4 !text-[11px] disabled:opacity-60" data-testid="blog-autopilot-run">
              {autoRunning ? "Writing…" : "Run now"}
            </button>
          </div>
        </div>
      </div>

      {/* AI generator */}
      <div className="card-dark p-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-[var(--if-gold)]" />
          <span className="font-mono-label text-[var(--if-gold)]">AI Writing Assistant</span>
        </div>
        <p className="text-xs text-white/55 mb-4" style={{ fontFamily: "Inter, sans-serif" }}>
          Give it a topic and it drafts a full SEO-optimized article (title, meta tags, ~1,000 words). You review, edit, and publish.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input className="if-input flex-1" placeholder='e.g. "How to remove a charge-off from your credit report"' value={topic}
            onChange={(e) => setTopic(e.target.value)} data-testid="blog-ai-topic" />
          <button onClick={generate} disabled={generating} className="btn-gold !py-2.5 !px-5 !text-[12px] disabled:opacity-60 shrink-0" data-testid="blog-ai-generate">
            {generating ? "Writing… (~30s)" : <><Sparkles className="w-4 h-4" /> Generate Draft</>}
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-outline !py-2.5 !px-5 !text-[12px] shrink-0" data-testid="blog-new-post">
            <Plus className="w-4 h-4" /> Blank Post
          </button>
        </div>
      </div>

      {/* Posts list */}
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="card-dark p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`blog-row-${p.slug}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display text-white text-base">{p.title}</span>
                <span className={`px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-semibold ${p.status === "published" ? "bg-green-500/15 text-green-300" : "bg-amber-500/15 text-amber-300"}`}>
                  {p.status}
                </span>
              </div>
              <div className="text-[11px] text-white/45 mt-1" style={{ fontFamily: "Inter, sans-serif" }}>
                /blog/{p.slug} · {p.category} · {p.read_time} · updated {String(p.updated_at).slice(0, 10)}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => togglePublish(p)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-[11px] transition-all ${p.status === "published" ? "border-amber-400/40 text-amber-300 hover:bg-amber-400/10" : "border-green-500/40 text-green-300 hover:bg-green-500/10"}`}
                data-testid={`blog-toggle-${p.slug}`}>
                {p.status === "published" ? <><EyeOff className="w-3.5 h-3.5" /> Unpublish</> : <><Globe className="w-3.5 h-3.5" /> Publish</>}
              </button>
              <button onClick={() => setEditing({ ...EMPTY, ...p })} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-white/70 text-[11px] hover:border-[var(--if-gold)]/50 transition-all" data-testid={`blog-edit-${p.slug}`}>
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              <button onClick={() => remove(p)} className="text-red-300/70 hover:text-red-300 p-1.5" title="Delete" data-testid={`blog-delete-${p.slug}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="text-white/45 text-sm" style={{ fontFamily: "Inter, sans-serif" }}>No posts yet.</p>}
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[120] flex items-start justify-center px-4 py-8 overflow-y-auto" data-testid="blog-editor-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-3xl card-dark p-7 my-auto">
            <button onClick={() => setEditing(null)} className="absolute top-3 right-3 text-white/40 hover:text-white p-2" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
            <h3 className="font-display text-2xl text-white mb-5">{editing.id ? "Edit Post" : "New Post"}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="font-mono-label text-white/50 block mb-1">Title</label>
                <input className="if-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="blog-editor-title" />
              </div>
              <div>
                <label className="font-mono-label text-white/50 block mb-1">URL slug</label>
                <input className="if-input" placeholder="auto from title" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
              </div>
              <div>
                <label className="font-mono-label text-white/50 block mb-1">Category</label>
                <select className="if-input" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-label text-white/50 block mb-1">Excerpt (shown on cards)</label>
                <input className="if-input" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-label text-white/50 block mb-1">Cover image URL</label>
                <input className="if-input" placeholder="https://…" value={editing.cover_image} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-label text-white/50 block mb-1">Content (markdown — use ## for headings)</label>
                <textarea className="if-input min-h-[280px] font-mono text-[13px]" value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} data-testid="blog-editor-content" />
              </div>
              <div>
                <label className="font-mono-label text-white/50 block mb-1">SEO meta title (≤60 chars)</label>
                <input className="if-input" maxLength={70} value={editing.meta_title} onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })} />
              </div>
              <div>
                <label className="font-mono-label text-white/50 block mb-1">SEO meta description (≤155 chars)</label>
                <input className="if-input" maxLength={160} value={editing.meta_description} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} />
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer" style={{ fontFamily: "Inter, sans-serif" }}>
                <input type="checkbox" checked={editing.status === "published"} onChange={(e) => setEditing({ ...editing, status: e.target.checked ? "published" : "draft" })} data-testid="blog-editor-publish" />
                Published (visible on the site)
              </label>
              <div className="flex gap-2">
                <button onClick={() => setEditing(null)} className="btn-outline !py-2 !px-4 !text-[11px]">Cancel</button>
                <button onClick={save} disabled={saving} className="btn-gold !py-2 !px-5 !text-[11px] disabled:opacity-60" data-testid="blog-editor-save">
                  {saving ? "Saving…" : "Save Post"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
